import { HttpApiEndpoint, HttpApiGroup } from "@effect/platform";
import * as S from "effect/Schema";
import { isCalendarUrlOrEmpty } from "@pima/utils";
import { PrincipalMiddleware } from "./principal.js";
import { UserLanguageSchema } from "./column-schemas.js";
import { ChangePrivateEmail, GetPrivateEmail } from "./private-email.js";
/**
 * The authenticated caller's account fields returned by profile updates.
 * `CurrentPrincipal` is provided once the middleware accepts the request.
 * `tfa_enabled` reports presence of `tfa_secret` without ever putting the
 * secret itself on the wire.
 */
export const Me = S.Struct({
    id: S.UUID,
    email: S.String,
    first_name: S.NullOr(S.String),
    last_name: S.NullOr(S.String),
    language: UserLanguageSchema,
    client_id: S.NullOr(S.UUID),
    last_page: S.NullOr(S.String),
    tfa_enabled: S.Boolean,
});
/** Auth-store fields for the caller, excluding credentials and provider secrets. */
export const SessionAccount = S.Struct({
    ...Me.fields,
    role: S.NullOr(S.UUID),
    title: S.NullOr(S.String),
    provider: S.NullOr(S.String),
    status: S.String,
    email_notifications: S.Boolean,
    metadata: S.Record({ key: S.String, value: S.Unknown }),
    contract_types: S.Array(S.String),
});
export const GetSessionAccount = HttpApiEndpoint.get("getAccount", "/session/account").addSuccess(S.Struct({ data: SessionAccount }));
/** Fields the authenticated caller may change about themselves. */
export const UpdateMePayload = S.Struct({
    first_name: S.optional(S.String),
    last_name: S.optional(S.String),
    email: S.optional(S.NonEmptyString),
    language: S.optional(UserLanguageSchema),
    profile_picture: S.optional(S.Union(S.UUID, S.Literal(""))),
    calendar: S.optional(S.NullOr(S.String.pipe(S.filter(isCalendarUrlOrEmpty, {
        message: () => "calendar must be an http(s) link or empty",
    })))),
    official_title: S.optional(S.String),
    onboarding: S.optional(S.Boolean),
    has_visited_risk_center: S.optional(S.Boolean),
    skip_add_employees_onboarding: S.optional(S.Boolean),
});
export const UpdateLastPagePayload = S.Struct({
    last_page: S.NonEmptyString,
});
/** The caller's own email is already taken by another account. */
export const EmailTaken = S.Struct({ _tag: S.Literal("EmailTaken") });
/**
 * A new account's brands are unknown or missing (`contract_types`), or the
 * address is a placeholder no invitation may go to (`email`). A malformed
 * address never reaches this: the request schema refuses it while decoding.
 */
export const InvalidUserInput = S.Struct({
    _tag: S.Literal("InvalidUserInput"),
    field: S.Literal("contract_types", "email"),
});
export const TfaGeneratePayload = S.Struct({
    password: S.NonEmptyString,
});
export const TfaSecret = S.Struct({
    secret: S.String,
    otpauth_url: S.String,
});
export const TfaEnablePayload = S.Struct({
    secret: S.NonEmptyString,
    otp: S.NonEmptyString,
});
export const TfaDisablePayload = S.Struct({
    otp: S.NonEmptyString,
});
/** The password on `tfaGenerate` didn't match the caller's own account. */
export const InvalidCredentials = S.Struct({
    _tag: S.Literal("InvalidCredentials"),
});
/** `tfaEnable` called while a secret is already set; disable first. */
export const TfaAlreadyEnabled = S.Struct({
    _tag: S.Literal("TfaAlreadyEnabled"),
});
/** `tfaDisable` called with no secret set. */
export const TfaNotEnabled = S.Struct({ _tag: S.Literal("TfaNotEnabled") });
/** The `otp` on `tfaEnable`/`tfaDisable` didn't match the secret. */
export const InvalidOtp = S.Struct({ _tag: S.Literal("InvalidOtp") });
const Ok = S.Struct({ ok: S.Literal(true) });
export const UpdateMe = HttpApiEndpoint.post("updateMe", "/session/me")
    .setPayload(UpdateMePayload)
    .addSuccess(S.Struct({ data: Me }))
    .addError(EmailTaken, { status: 409 });
export const UpdateLastPage = HttpApiEndpoint.post("updateLastPage", "/session/last-page")
    .setPayload(UpdateLastPagePayload)
    .addSuccess(S.Struct({ data: Ok }));
export const TfaGenerate = HttpApiEndpoint.post("tfaGenerate", "/session/tfa/generate")
    .setPayload(TfaGeneratePayload)
    .addSuccess(S.Struct({ data: TfaSecret }))
    .addError(InvalidCredentials, { status: 401 })
    .addError(TfaAlreadyEnabled, { status: 409 });
export const TfaEnable = HttpApiEndpoint.post("tfaEnable", "/session/tfa/enable")
    .setPayload(TfaEnablePayload)
    .addSuccess(S.Struct({ data: Ok }))
    .addError(TfaAlreadyEnabled, { status: 409 })
    .addError(InvalidOtp, { status: 400 });
export const TfaDisable = HttpApiEndpoint.post("tfaDisable", "/session/tfa/disable")
    .setPayload(TfaDisablePayload)
    .addSuccess(S.Struct({ data: Ok }))
    .addError(TfaNotEnabled, { status: 400 })
    .addError(InvalidOtp, { status: 400 });
export const sessionGroup = HttpApiGroup.make("session")
    .add(GetSessionAccount)
    .add(UpdateMe)
    .add(UpdateLastPage)
    .add(TfaGenerate)
    .add(TfaEnable)
    .add(TfaDisable)
    .add(GetPrivateEmail)
    .add(ChangePrivateEmail)
    .middleware(PrincipalMiddleware);
//# sourceMappingURL=session.js.map